import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import fiscalPrinterService from '../services/fiscalPrinterService';
import { transactionService } from '../services/transactionService';
import { distributeDiscountByVat } from '../services/fiscal/itemHelpers';
import { ocenOstrzezenia } from '../services/fiscal/receiptContract';
import {
  kontraktWlaczony, pobierzKontrakt, wyslijRaport, dosliZalegleRaporty, wynikZWeryfikacji
} from '../services/fiscalReceiptService';
import { zapiszDrukWToku, wyczyscDrukWToku } from '../services/fiscal/drukWToku';

/**
 * DRUK W TOKU (pamiec przegladarki, NIE protokol drukarki).
 *
 * Miedzy wyslaniem pakietow a potwierdzeniem drukarki mija ok. 10 s. Gdy w tym
 * oknie strona kasy sie zrestartuje (F5, stara wersja po wdrozeniu), wynik
 * druku przepada, a status w bazie zostaje 0 - patrz drukWToku.js. Dlatego
 * KAZDE wyslanie paragonu jest opakowane: znacznik w localStorage tuz PRZED
 * wywolaniem serwisu drukarki i kasowanie ZARAZ po wyniku (yes/no/unknown),
 * takze gdy serwis rzuci. Samo wywolanie i jego wynik sa nietkniete.
 */
const zDrukiemWToku = async (dane, wyslij) => {
  zapiszDrukWToku(dane);
  try {
    return await wyslij();
  } finally {
    wyczyscDrukWToku();
  }
};

/**
 * Rozroznia BRAK stawki VAT od stawki ZEROWEJ.
 * parseFloat(x) || 23 zamienialo 0% na 23% (0 || 23 === 23) -> blad VAT na paragonie.
 * Tu: tylko faktyczny brak (null/undefined/""/NaN) daje domyslne 23; 0 zostaje 0.
 * @param {*} raw - surowa wartosc item.stawka_vat
 * @returns {number} stawka VAT (0 to prawidlowa wartosc, nie fallback)
 */
const resolveVatRate = (raw) => {
  const p = parseFloat(raw);
  return (raw === null || raw === undefined || raw === '' || Number.isNaN(p)) ? 23 : p;
};

/**
 * Okno, ktore ZATRZYMUJE ekran kasy. Uzywamy natywnego alert/confirm - tak samo
 * jak reszta aplikacji (useAccountingData.js, FiscalPrinterTab.jsx) - bo dymek
 * react-hot-toast znika sam i przy kasie w godzinach szczytu nikt go nie czyta.
 * To wlasnie ten dymek sprawil, ze "krytyczne" ostrzezenia serwera nie mialy
 * zadnego skutku.
 *
 * Brak window.confirm (test, skrypt, przegladarka bez okien) NIE oznacza zgody:
 * bez potwierdzenia nie drukujemy. Domyslna odpowiedz musi byc ta bezpieczna.
 */
const pokazBlokade = (tekst) => {
  if (typeof window !== 'undefined' && typeof window.alert === 'function') {
    try {
      window.alert(tekst);
      return true;
    } catch (_) {
      return false;
    }
  }
  return false;
};

const zapytajCzyDrukowac = (tekst) => {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return false;
  try {
    return window.confirm(tekst) === true;
  } catch (_) {
    return false;
  }
};

/**
 * Slad w bazie dla paragonu, ktorego NIE wyslalismy do drukarki.
 *
 * ZAPIS 1 zalozyl juz wpis w fiskalne_wydruki. Gdyby po odmowie druku nikt nie
 * odeslal raportu, wpis zostalby na zawsze w stanie "wydany" i audyt nie
 * odroznilby paragonu przerwanego swiadomie od takiego, ktory sie zawiesil.
 * 'niewydrukowany' jest tu zgodne z prawda: do drukarki nie poszedl ani jeden
 * bajt. Pozniejszy udany wydruk ma wyzsza wage i spokojnie to nadpisze.
 */
const zglosZaniechanie = async (wydrukId, kontrakt, kod, opis) => {
  if (!wydrukId) return;
  try {
    await wyslijRaport(wydrukId, {
      wynik: 'niewydrukowany',
      payload_hash: kontrakt?.kontrola?.payload_hash || null,
      printoutno: null,
      numer_drukarki: null,
      kod_bledu: kod,
      opis_bledu: opis,
      czas_druku_ms: null,
      pakietow: 0,
      bajtow_wyslanych: 0,
      transkrypt: [],
    });
  } catch (e) {
    console.error('[FISCAL-KONTRAKT] nie udalo sie zapisac sladu odmowy druku:', e);
  }
};

/**
 * Hook do obslugi drukarki fiskalnej.
 *
 * DWIE SCIEZKI DRUKU, przelaczane flaga localStorage.fiskalny_kontrakt === '1':
 *
 *   SERWEROWA (kontrakt 1.0, docelowa) - przegladarka pobiera GOTOWY paragon
 *     z POST /api/transactions/<id>/fiscal-receipt, sklada z niego pakiety XML,
 *     wysyla przez Web Serial i odsyla raport z kabla. Nie liczy rabatu, nie mapuje
 *     stawek, nie sklada nazw, nie zaokragla kwot.
 *
 *   STARA (liczaca w przegladarce) - zostawiona na czas przejsciowy jako punkt
 *     odniesienia dla zlotej proby (diff bajtow stara vs nowa) i jako droga powrotu,
 *     gdyby wydruk probny na sprzecie wykazal roznice. Do usuniecia po przejsciu
 *     produkcji na sciezke serwerowa.
 *
 * Gdy sciezka serwerowa jest WLACZONA, a serwer nie odda kontraktu - NIE DRUKUJEMY.
 * Cichy powrot do liczenia z koszyka byloby powrotem do bledu, ktory zaniza paragony
 * (transakcja 13292: 3.90 zl na papierze przy 31.92 zl zaplaty).
 */
const useFiscalPrinter = () => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoPrint, setAutoPrint] = useState(true);

  // Auto-polaczenie przy montowaniu z cleanup
  useEffect(() => {
    let isMounted = true;
    let cancelled = false;
    let inFlight = false;

    // Po TWARDYM odswiezeniu (Ctrl+Shift+R) poprzednia instancja strony trzymala
    // port jako zasob WYLACZNY. System zwalnia urzadzenie USB z opoznieniem
    // (kilkaset ms), wiec pierwsze port.open() tuz po reloadzie czesto rzuca
    // bledem. Dlatego ponawiamy kilka razy z rosnacym odstepem, zamiast poddac
    // sie po jednej probie (wtedy drukarka zostawala rozlaczona az do reczna).
    const tryAutoConnect = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const MAX_ATTEMPTS = 5;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS && !cancelled; attempt++) {
          let result;
          try {
            result = await fiscalPrinterService.autoConnect();
          } catch (err) {
            result = { success: false, message: err?.message };
          }
          if (!isMounted) return;
          setConnected(fiscalPrinterService.connected);
          if (result && result.success) return;
          // Retry nic nie da, jesli uzytkownik nigdy nie nadal portu w tej
          // przegladarce albo brak wsparcia Web Serial.
          if (result && (result.message === 'Brak zapamietanych portow'
                      || result.message === 'Web Serial API nie wspierane')) {
            return;
          }
          // Odczekaj przed kolejna proba (0.5s, 1s, 1.5s, 2s...).
          await new Promise(r => setTimeout(r, 500 * attempt));
        }
      } finally {
        inFlight = false;
      }
    };
    tryAutoConnect();

    // Gdy karta znow staje sie aktywna (powrot do zakladki, wybudzenie) i drukarka
    // nadal rozlaczona - sprobuj jeszcze raz (np. port zwolnil sie w miedzyczasie).
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !fiscalPrinterService.connected) {
        tryAutoConnect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    // Subskrypcja realnego stanu polaczenia (heartbeat + nasluch USB disconnect/reconnect),
    // zeby UI odzwierciedlalo wypiecie/wpiecie drukarki bez odswiezania strony.
    let unsubscribe = null;
    if (typeof fiscalPrinterService.subscribeConnection === 'function') {
      unsubscribe = fiscalPrinterService.subscribeConnection((status) => {
        if (isMounted) setConnected(!!status.connected);
      });
    }

    return () => {
      isMounted = false;
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Zalegle raporty z kabla (druk sie udal, ale padla siec przy odsylaniu).
  // Bez tego kazde zerwanie polaczenia po druku kasuje jedyny slad tego,
  // co poszlo na papier - a to wlasnie brak sladu uniemozliwial diagnoze.
  useEffect(() => {
    if (!kontraktWlaczony()) return;
    dosliZalegleRaporty().catch(() => {});
  }, []);

  // Sprawdz czy wspierane
  const isSupported = useCallback(() => {
    return fiscalPrinterService.isSupported();
  }, []);

  // Pobierz protokol
  const getProtocol = useCallback(() => {
    return fiscalPrinterService.getProtocol();
  }, []);

  // Polacz z drukarka
  const connect = useCallback(async () => {
    if (!fiscalPrinterService.isSupported()) {
      toast.error('Web Serial API nie jest wspierane. Uzyj Chrome lub Edge!');
      return false;
    }

    try {
      setLoading(true);
      const result = await fiscalPrinterService.connect();
      // connect() moze sie teraz udac technicznie (port otwarty) ale ODRZUCIC
      // polaczenie, gdy na porcie nie ma drukarki (brak odpowiedzi na <dle/>).
      // Nie wolno wtedy pokazac "polaczono" - to byl falszywy pozytyw na zlym
      // porcie COM. Ufamy fladze success ze warstwy polaczenia.
      if (result && result.success === false) {
        toast.error(result.message || 'Nie wykryto drukarki na wybranym porcie.');
        setConnected(false);
        return false;
      }
      setConnected(true);
      return true;
    } catch (err) {
      console.error('Blad polaczenia z drukarka:', err);
      toast.error(`Blad polaczenia z drukarka: ${err.message}`);
      setConnected(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Rozlacz drukarke
  const disconnect = useCallback(async () => {
    try {
      await fiscalPrinterService.disconnect();
      setConnected(false);
    } catch (err) {
      console.error('Blad rozlaczania drukarki:', err);
    }
  }, []);

  /**
   * SCIEZKA SERWEROWA (kontrakt 1.0). Przegladarka jest kablem:
   *   1. pobiera GOTOWY paragon z serwera (ZAPIS 1 zaklada slad proby druku),
   *   2. sklada z niego pakiety XML i wysyla przez port,
   *   3. odsyla raport z kabla (ZAPIS 2 ustawia status fiskalizacji atomowo).
   *
   * Zero arytmetyki po drodze. Zero czytania koszyka i appliedDiscounts.
   *
   * @param {number} transactionId - transakcja MUSI byc juz sfinalizowana
   * @param {string} powod - 'sprzedaz' (druk po finalizacji) | 'ponowny' (z listy)
   */
  const drukujZKontraktu = useCallback(async (transactionId, powod) => {
    // Zalegle raporty z poprzednich wydrukow (padl internet) - w tle, nie blokuja druku.
    dosliZalegleRaporty().catch(() => {});

    // --- 1. Kontrakt z serwera -------------------------------------------
    const odp = await pobierzKontrakt(transactionId, { powod });
    if (!odp.ok) {
      // SCIEZKA AWARYJNA: NIE drukujemy z koszyka. Cichy powrot do liczenia
      // w przegladarce jest dokladnie tym bledem, ktory zanizyl paragon 13292
      // (3.90 zl na papierze przy 31.92 zl zaplaty). Lepiej brak paragonu,
      // ktory kasjerka widzi, niz zly paragon, ktorego nikt nie zauwazy.
      console.error('[FISCAL-KONTRAKT] serwer nie wydal paragonu:', odp.kod, odp.komunikat);
      toast.error(
        `Paragon NIE zostal wydrukowany: serwer nie wydal danych paragonu [${odp.kod}]. ${odp.komunikat}. `
        + 'Nie drukujemy z koszyka - powtorz fiskalizacje z listy transakcji, gdy problem minie.',
        { duration: 15000 }
      );
      return {
        success: false,
        printed: 'no',
        skipped: true,
        nieWyslano: true,
        retryable: true,
        errorCode: odp.kod,
        error: odp.komunikat,
        errorMessage: odp.komunikat,
      };
    }

    const kontrakt = odp.kontrakt;
    const wydrukId = kontrakt.wydruk_id ?? null;

    // --- 1a. BRAMKA OSTRZEZEN (przed wyslaniem czegokolwiek do drukarki) ---
    // Serwer nadaje kazdemu ostrzezeniu wage, a przegladarka wrzucala je dotad
    // do jednego dymka na 15 sekund - takze te "krytyczne". Tutaj waga wreszcie
    // ma skutek: blokada druku albo okno, ktore trzeba swiadomie potwierdzic.
    // Podzial i uzasadnienie: receiptContract.js, REAKCJA_NA_OSTRZEZENIE.
    const ocena = ocenOstrzezenia(kontrakt.ostrzezenia);

    if (ocena.blokujace.length > 0) {
      console.error('[FISCAL-KONTRAKT] ostrzezenia BLOKUJACE druk:', ocena.blokujace);
      pokazBlokade(ocena.tekstBlokady);
      const kody = ocena.blokujace.map(o => o.kod).join(', ');
      // Dymek jest tu TYLKO sladem na ekranie po zamknieciu okna - caly opis
      // kasjerka dostala w oknie, ktore musiala kliknac.
      toast.error(
        `Paragon NIE zostal wydrukowany - dane paragonu sa niepoprawne. `
        + `Popraw transakcje i wydrukuj ja ponownie z listy transakcji (${kody}).`,
        { duration: 30000 }
      );
      await zglosZaniechanie(wydrukId, kontrakt, 'OSTRZEZENIE_BLOKUJACE',
        `Druk wstrzymany przez kase: ${kody}`);
      return {
        success: false,
        printed: 'no',
        skipped: true,
        nieWyslano: true,
        // Ponowienie ma sens dopiero PO poprawieniu transakcji - samo klikniecie
        // "drukuj" jeszcze raz da dokladnie ten sam blokujacy kontrakt.
        retryable: false,
        errorCode: 'OSTRZEZENIE_BLOKUJACE',
        error: ocena.tekstBlokady,
        errorMessage: ocena.tekstBlokady,
        ostrzezenia: ocena,
      };
    }

    if (ocena.doPotwierdzenia.length > 0) {
      console.warn('[FISCAL-KONTRAKT] ostrzezenia do potwierdzenia:', ocena.doPotwierdzenia);
      if (!zapytajCzyDrukowac(ocena.tekstPotwierdzenia)) {
        toast.error(
          'Paragon NIE zostal wydrukowany - przerwano druk po ostrzezeniu. '
          + 'Popraw transakcje i wydrukuj ja ponownie z listy transakcji.',
          { duration: 12000 }
        );
        const kody = ocena.doPotwierdzenia.map(o => o.kod).join(', ');
        await zglosZaniechanie(wydrukId, kontrakt, 'KASJERKA_PRZERWALA',
          `Kasjerka nie potwierdzila druku mimo ostrzezen: ${kody}`);
        return {
          success: false,
          printed: 'no',
          skipped: true,
          nieWyslano: true,
          retryable: true,
          errorCode: 'KASJERKA_PRZERWALA',
          error: 'Kasjerka przerwala druk po ostrzezeniu',
          errorMessage: 'Kasjerka przerwala druk po ostrzezeniu',
          ostrzezenia: ocena,
        };
      }
    }

    // --- 2. Druk ----------------------------------------------------------
    const result = await zDrukiemWToku({
      transactionId,
      numer: kontrakt.naglowek?.numer_systemowy,
      kwota: kontrakt.sumy?.suma_fiskalna,
      sciezka: powod === 'ponowny' ? 'ponowny' : 'finalizacja',
    }, () => fiscalPrinterService.printReceiptFromContract(kontrakt));

    // --- 3. Komunikaty dla kasjerki (trzy stany bez zmian) ----------------
    const printoutNo = result.printoutno ?? result.printoutNumber;
    if (result.printed === 'yes') {
      const ackParts = ['Paragon fiskalny wydrukowany'];
      if (printoutNo) ackParts.push(`#${printoutNo}`);
      toast.success(ackParts.join(' '));
      if (result.paperNearEnd) {
        toast('Uwaga: papier sie konczy - wymien przed nastepna transakcja', { icon: 'warning', duration: 8000 });
      }
    } else if (result.printed === 'no') {
      const errMsg = result.errorMessage || result.error || 'Nieznany blad';
      console.error('Drukarka: paragon NIE wydrukowany:', result);
      toast.error(`Paragon NIE zostal wydrukowany: ${errMsg}. Ponow (retry bezpieczny).`, { duration: 12000 });
    } else {
      console.warn('Drukarka: brak potwierdzenia wydruku (unknown):', result);
      toast('Nie potwierdzono wydruku paragonu. Sprawdz drukarke przed ponowieniem.', { icon: '⚠️', duration: 12000 });
    }

    // Ostrzezenia drobne (papier jest dobry, zostaje tylko slad dla serwisu).
    // Te powazne zostaly juz obsluzone w bramce PRZED drukiem - blokada albo
    // okno z potwierdzeniem - wiec nie powtarzamy ich w dymku.
    if (ocena.tekstInformacji) {
      console.warn('[FISCAL-KONTRAKT] ostrzezenia serwera:', ocena.informacyjne);
      toast(ocena.tekstInformacji, { icon: '⚠️', duration: 15000 });
    }
    if (ocena.doPotwierdzenia.length > 0) {
      // Papier juz wyszedl z bledem, ktory kasjerka swiadomie zaakceptowala.
      // Przypomnienie zostaje na ekranie, zeby zgloszenie do kierownika nie
      // przepadlo razem z oknem potwierdzenia.
      toast(
        `Paragon wydrukowany mimo ostrzezenia - zglos to kierownikowi (${ocena.doPotwierdzenia.map(o => o.kod).join(', ')})`,
        { icon: '⚠️', duration: 20000 }
      );
    }

    // --- 4. ZAPIS 2: raport z kabla ---------------------------------------
    // Ten sam endpoint ustawia pos_transakcje.fiskalizacja, wiec status i slad
    // powstaja atomowo. Starego PUT /fiscal-status uzywamy TYLKO awaryjnie.
    let statusZapisany = false;
    if (wydrukId) {
      const raport = {
        wynik: wynikZWeryfikacji(result.printed),
        payload_hash: result.payloadHash || kontrakt.kontrola?.payload_hash,
        printoutno: printoutNo || null,
        // numer_drukarki zostaje puste CELOWO: weryfikacja nie zwraca numeru
        // urzadzenia, a wpisanie tam numeru transakcji z drukarki (atrybut
        // number= z lasttransaction) zaklamaloby audyt. Numer transakcji jest
        // zachowany w transkrypcie - patrz wpis 'log' po weryfikacji.
        numer_drukarki: null,
        kod_bledu: result.errorCode || null,
        opis_bledu: result.errorMessage || result.error || null,
        czas_druku_ms: result.czasDrukuMs ?? null,
        pakietow: result.pakietow ?? null,
        bajtow_wyslanych: result.bajtowWyslanych ?? null,
        transkrypt: result.transkrypt || [],
      };
      const raportOdp = await wyslijRaport(wydrukId, raport);
      statusZapisany = raportOdp.ok || !!raportOdp.zakolejkowany;
      if (!raportOdp.ok && !raportOdp.zakolejkowany) {
        console.error('[FISCAL-KONTRAKT] raport z druku odrzucony:', raportOdp.komunikat);
      }
    }

    // Awaryjne wyjscie: brak wydruk_id (serwer nie zdolal zalozyc sladu -
    // ostrzezenie LOG_NIEDOSTEPNY) albo raport odrzucony na stale.
    // UWAGA: stary endpoint zna tylko dwa stany (1 / -1), wiec 'nie_wiadomo'
    // zapisze sie tu jako -1 z opisem. Trzy stany zachowuje ZAPIS 2 powyzej.
    if (!statusZapisany && transactionId) {
      try {
        if (result.printed === 'yes') {
          await transactionService.updateFiscalStatus(transactionId, true);
        } else {
          const errMsg = result.errorMessage || result.error || 'Drukarka nie potwierdzila wydruku';
          const bladDesc = result.errorCode ? `[${result.errorCode}] ${errMsg}` : errMsg;
          await transactionService.updateFiscalStatus(transactionId, false, bladDesc);
        }
      } catch (e) {
        console.error('Blad aktualizacji statusu fiskalizacji (wyjscie awaryjne):', e);
      }
    }

    return result;
  }, []);

  // Drukuj paragon
  const printReceipt = useCallback(async (cartItems, totalAmount, cashierName, options = {}) => {
    const { transactionId, customerNip, splitPaymentsData, documentNumber, paymentMethod, appliedDiscounts = [] } = options;

    // Sprawdz polaczenie
    if (!fiscalPrinterService.connected) {
      toast.error('Drukarka fiskalna nie jest polaczona! Paragon NIE zostal wydrukowany. Polacz drukarke uzywajac przycisku w naglowku i sprobuj ponownie z listy transakcji.');
      return { success: false, skipped: true, error: 'Drukarka nie polaczona' };
    }

    // Sprawdz port
    if (!fiscalPrinterService.port || !fiscalPrinterService.port.writable) {
      toast.error('Port drukarki fiskalnej nie jest gotowy! Paragon NIE zostal wydrukowany. Sprobuj rozlaczyc i polaczyc drukarke ponownie.');
      fiscalPrinterService.connected = false;
      setConnected(false);
      return { success: false, skipped: true, error: 'Port drukarki nie gotowy' };
    }

    // SCIEZKA SERWEROWA - domyslnie wylaczona, wlacza ja
    // localStorage.setItem('fiskalny_kontrakt', '1'). Wymaga numeru transakcji:
    // paragon buduje sie z ZAPISANEJ transakcji, nie z koszyka.
    if (kontraktWlaczony() && fiscalPrinterService.getProtocol() === 'xml') {
      if (!transactionId) {
        toast.error('Paragon NIE zostal wydrukowany: brak numeru zapisanej transakcji. Zglos to i wydrukuj z listy transakcji.', { duration: 12000 });
        return { success: false, skipped: true, nieWyslano: true, error: 'Brak transactionId dla sciezki serwerowej' };
      }
      try {
        return await drukujZKontraktu(transactionId, 'sprzedaz');
      } catch (err) {
        console.error('Blad drukowania paragonu (sciezka serwerowa):', err);
        toast.error(`Blad drukowania paragonu: ${err.message}`);
        return { success: false, error: err.message };
      }
    }

    // ---- SCIEZKA STARA (liczaca w przegladarce) - na czas przejsciowy ----
    try {
      // Zabezpieczenie: upewnij się że mamy tablice
      const items = Array.isArray(cartItems) ? cartItems : [];
      const discounts = Array.isArray(appliedDiscounts) ? appliedDiscounts : [];

      // RABAT NA PARAGONIE = TO, CO SERWER ZAPISAL (pos_transakcje.rabat_kwota).
      // Zamrozone appliedDiscounts[].kwota_rabatu z chwili nadania rabatu bylo
      // zrodlem pierwotnego bledu (paragon 3.90 zl przy 31.92 zl zaplaty -
      // koszyk zmalal, wyswietlacz liczyl na zywo, druk wzial stara kwote).
      // Transakcja jest w tym miejscu JUZ sfinalizowana, wiec rabat_kwota
      // w bazie to dokladnie kwota rozliczona z klientem.
      let totalDiscountAmount = discounts.reduce((sum, d) => sum + (parseFloat(d.kwota_rabatu) || 0), 0);
      if (transactionId) {
        try {
          const txResp = await transactionService.getTransactionDetails(transactionId);
          const rabatZBazy = txResp?.data?.rabat_kwota;
          if (txResp?.success && rabatZBazy !== undefined && rabatZBazy !== null) {
            totalDiscountAmount = parseFloat(rabatZBazy) || 0;
          } else {
            console.warn('[FISCAL] brak rabat_kwota z bazy - druk liczy rabat z appliedDiscounts (fallback)');
          }
        } catch (e) {
          console.warn('[FISCAL] nie udalo sie pobrac rabat_kwota z bazy - fallback na appliedDiscounts:', e?.message);
        }
      }

      // Rabat per stawka VAT (zgodnie z drukarka fiskalna)
      const itemsForDistribution = items.map(item => ({
        quantity: parseFloat(item.quantity) || 1,
        price: parseFloat(item.price) || parseFloat(item.cena) || 0,
        vat_rate: resolveVatRate(item.stawka_vat),
        is_deposit: parseFloat(item.stawka_vat) === -2 || item.typ_produktu === 'kaucja',
        typ_produktu: item.typ_produktu,
        wykluczony_z_rabatow: item.wykluczony_z_rabatow,
      }));
      const perItemDiscounts = distributeDiscountByVat(itemsForDistribution, totalDiscountAmount);

      // Przygotuj dane paragonu
      const receiptData = {
        items: items.map((item, idx) => {
          const vatRate = resolveVatRate(item.stawka_vat);
          const price = parseFloat(item.price) || parseFloat(item.cena) || 0;
          const qty = parseFloat(item.quantity) || 1;
          const isDeposit = vatRate === -2 || item.typ_produktu === 'kaucja';
          const itemDiscount = perItemDiscounts[idx] || 0;

          return {
            id: item.id ?? item.produkt_id ?? null,  // do unikalnego prefiksu fiskalnego
            name: item.name || item.nazwa || 'Produkt',
            quantity: qty,
            price: price,
            vat_rate: vatRate,
            is_deposit: isDeposit,
            typ_produktu: isDeposit ? 'kaucja' : 'produkt',
            discount: itemDiscount > 0 ? {
              amount: itemDiscount,
              name: discounts.length > 0 ? (discounts[0].nazwa || 'Rabat') : 'Rabat'
            } : null
          };
        }),
        total: totalAmount,
        // KONTROLA KWOT PRZED DRUKIEM (ustalenie A): totalAmount pochodzi
        // z odpowiedzi finalize (kwota zapisana przez serwer). receiptXML
        // porownuje ja CO DO GROSZA z suma wyliczona z pozycji ZANIM wysle
        // pierwszy bajt do drukarki - rozjazd zatrzymuje druk zamiast wyjsc
        // na papier. Obie strony licza tym samym silnikiem rabatu, wiec
        // rozjazd to prawdziwy blad, nie codziennosc.
        expected_total: totalAmount,
        payment_type: splitPaymentsData ? 'dzielona' : paymentMethod,
        cashier: cashierName || '',
        nip_nabywcy: customerNip || null,
        // K5: kilka kart podarunkowych (N wpisow 'kupon') = JEDNA pozycja
        // platnosci "Kupon" na sume - tak samo buduje to serwerowy
        // fiscal_receipt_builder. Pozostale metody sa w splicie pojedyncze.
        payments: splitPaymentsData ? Object.values(
          splitPaymentsData.filter(p => p.amount > 0).reduce((acc, p) => {
            if (acc[p.method]) {
              acc[p.method].amount = Math.round((acc[p.method].amount + p.amount) * 100) / 100;
            } else {
              acc[p.method] = { type: p.method, amount: p.amount };
            }
            return acc;
          }, {})
        ) : [],
        document_number: documentNumber || null
      };

      const result = await zDrukiemWToku({
        transactionId,
        numer: documentNumber,
        kwota: totalAmount,
        sciezka: 'finalizacja',
      }, () => fiscalPrinterService.printReceipt(receiptData));

      // KONTRAKT weryfikacji druku: result.printed in {yes, no, unknown}.
      // fiskalizacja=1 (mark-fiscalized) TYLKO gdy printed === 'yes'.
      // NIGDY fiskalizacja=1 dla 'no' / 'unknown' - transakcja zostaje niezafiskalizowana (0).
      const printoutNo = result.printoutno ?? result.printoutNumber;
      if (result.printed === 'yes') {
        // Drukarka POTWIERDZILA nasz paragon -> na pewno wyszedl.
        const ackParts = ['Paragon fiskalny wydrukowany'];
        if (printoutNo) ackParts.push(`#${printoutNo}`);
        toast.success(ackParts.join(' '));
        if (result.paperNearEnd) {
          toast('Uwaga: papier sie konczy - wymien przed nastepna transakcja', { icon: 'warning', duration: 8000 });
        }

        if (transactionId) {
          try {
            await transactionService.updateFiscalStatus(transactionId, true);
          } catch (updateErr) {
            console.error('Blad aktualizacji statusu fiskalizacji:', updateErr);
          }
        }
      } else if (result.printed === 'no') {
        // Drukarka odpowiedziala ale paragonu NIE ma -> na pewno NIE wyszedl. Bezpieczne ponowienie.
        console.error('Drukarka: paragon NIE wydrukowany:', result);
        const errMsg = result.errorMessage || result.error || 'Nieznany blad';
        toast.error(`Paragon NIE zostal wydrukowany: ${errMsg}. Ponow (retry bezpieczny).`, { duration: 12000 });

        if (transactionId) {
          try {
            const bladDesc = result.errorCode ? `[${result.errorCode}] ${errMsg}` : errMsg;
            await transactionService.updateFiscalStatus(transactionId, false, bladDesc);
          } catch (e) {}
        }
      } else {
        // 'unknown' (lub brak potwierdzenia) -> niepewne. NIE fiskalizuj, NIE ponawiaj na slepo.
        console.warn('Drukarka: brak potwierdzenia wydruku (unknown):', result);
        const errMsg = result.errorMessage || result.error || 'Drukarka nie potwierdzila wydruku';
        toast('Nie potwierdzono wydruku paragonu. Sprawdz drukarke przed ponowieniem.', { icon: '⚠️', duration: 12000 });

        if (transactionId) {
          try {
            const bladDesc = result.errorCode ? `[${result.errorCode}] ${errMsg}` : errMsg;
            await transactionService.updateFiscalStatus(transactionId, false, bladDesc);
          } catch (e) {}
        }
      }

      return result;
    } catch (err) {
      console.error('Blad drukowania paragonu:', err);
      if (transactionId) {
        try {
          await transactionService.updateFiscalStatus(transactionId, false, err.message);
        } catch (e) {}
      }
      toast.error(`Blad drukowania paragonu: ${err.message}`);
      return { success: false, error: err.message };
    }
  }, [drukujZKontraktu]);

  // Fiskalizacja z listy transakcji
  const printFromTransactionList = useCallback(async (transaction) => {

    if (!fiscalPrinterService.connected) {
      toast.error('Drukarka fiskalna nie jest polaczona! Polacz drukarke uzywajac przycisku w naglowku.');
      return;
    }

    // SCIEZKA SERWEROWA - ponowny druk juz zapisanej transakcji.
    // Cale liczenie kwot jest po stronie serwera (kontrakt 1.0).
    if (kontraktWlaczony() && fiscalPrinterService.getProtocol() === 'xml') {
      try {
        return await drukujZKontraktu(transaction.id, 'ponowny');
      } catch (err) {
        console.error('Blad fiskalizacji (sciezka serwerowa):', err);
        toast.error(`Blad fiskalizacji: ${err.message}`);
        return { success: false, error: err.message };
      }
    }

    // ---- SCIEZKA STARA (liczaca w przegladarce) - na czas przejsciowy ----
    try {
      const response = await transactionService.getTransactionDetails(transaction.id);

      if (!response.success || !response.data) {
        toast.error('Nie mozna pobrac szczegolów transakcji');
        return;
      }

      const txData = response.data;
      const items = txData.items || [];
      const rabaty = txData.rabaty || [];
      const tx = txData;

      if (items.length === 0) {
        toast.error('Transakcja nie ma pozycji do fiskalizacji');
        return;
      }

      // RABAT = pos_transakcje.rabat_kwota (naglowek transakcji) - jedyne
      // zrodlo kwoty rozliczonej z klientem. suma_rabatow_obliczona
      // (SUM z rabaty_uzycie) zostaje wylacznie jako fallback dla starych
      // wierszy bez rabat_kwota.
      const rabatKwotaBaza = tx.rabat_kwota;
      const sumaRabatow = (rabatKwotaBaza !== undefined && rabatKwotaBaza !== null)
        ? (parseFloat(rabatKwotaBaza) || 0)
        : (parseFloat(tx.suma_rabatow_obliczona)
           || rabaty.reduce((sum, r) => sum + (parseFloat(r.kwota_rabatu) || 0), 0));

      const nazwaRabatu = rabaty.length > 0 ? (rabaty[0].rabat_nazwa || 'Rabat') : 'Rabat';
      const nipNabywcy = tx.customer_nip || null;

      // Rabat per stawka VAT (zgodnie z drukarka fiskalna)
      const itemsForDistribution = items.map(item => ({
        quantity: parseFloat(item.ilosc || item.quantity) || 1,
        price: parseFloat(item.cena_jednostkowa || item.price) || 0,
        vat_rate: resolveVatRate(item.stawka_vat),
        is_deposit: parseFloat(item.stawka_vat) === -2 || item.typ_produktu === 'kaucja',
        typ_produktu: item.typ_produktu,
        wykluczony_z_rabatow: item.wykluczony_z_rabatow,
      }));
      const perItemDiscounts = distributeDiscountByVat(itemsForDistribution, sumaRabatow);

      const receiptItems = items.map((item, idx) => {
        const qty = parseFloat(item.ilosc || item.quantity) || 1;
        const price = parseFloat(item.cena_jednostkowa || item.price) || 0;
        const vatRate = resolveVatRate(item.stawka_vat);
        const isDeposit = vatRate === -2 || item.typ_produktu === 'kaucja';
        const itemDiscount = perItemDiscounts[idx] || 0;

        const result = {
          id: item.produkt_id ?? item.id ?? null,  // do unikalnego prefiksu fiskalnego
          name: item.nazwa_produktu || item.nazwa || item.name || 'Produkt',
          quantity: qty,
          price: price,
          vat_rate: vatRate,
          is_deposit: isDeposit,
          typ_produktu: isDeposit ? 'kaucja' : 'produkt'
        };

        if (itemDiscount > 0) {
          result.discount = {
            amount: itemDiscount,
            name: nazwaRabatu
          };
        }

        return result;
      });

      // Kwota paragonu = suma_brutto - rabat (to, co klient FAKTYCZNIE zaplacil).
      const sumaBrutto = parseFloat(tx.suma_brutto || tx.suma || tx.total_amount) || 0;
      const kwotaDoZaplaty = Math.round((sumaBrutto - sumaRabatow) * 100) / 100;

      const receiptData = {
        items: receiptItems,
        total: kwotaDoZaplaty,
        // Kontrola kwot przed drukiem (ustalenie A): rozjazd sumy pozycji
        // z kwota naglowka transakcji zatrzymuje druk PRZED pierwszym bajtem.
        expected_total: kwotaDoZaplaty,
        payment_type: tx.forma_platnosci || tx.metoda_platnosci || tx.payment_method || 'gotowka',
        cashier: tx.kasjer_login || tx.kasjer || tx.cashier || '',
        nip_nabywcy: nipNabywcy,
        document_number: tx.numer_paragonu || tx.numer_dokumentu || tx.document_number || `PA/${transaction.id}`
      };

      const result = await zDrukiemWToku({
        transactionId: transaction.id,
        numer: receiptData.document_number,
        kwota: kwotaDoZaplaty,
        sciezka: 'ponowny',
      }, () => fiscalPrinterService.printReceipt(receiptData));

      // KONTRAKT weryfikacji druku: result.printed in {yes, no, unknown}.
      // fiskalizacja=1 (mark-fiscalized) TYLKO gdy printed === 'yes'.
      const printoutNo = result.printoutno ?? result.printoutNumber;
      if (result.printed === 'yes') {
        // Drukarka POTWIERDZILA nasz paragon -> na pewno wyszedl.
        try {
          await transactionService.updateFiscalStatus(transaction.id, true);
          const ackParts = ['Paragon fiskalny wydrukowany'];
          if (printoutNo) ackParts.push(`#${printoutNo}`);
          toast.success(ackParts.join(' '));
          if (result.paperNearEnd) {
            toast('Uwaga: papier sie konczy - wymien przed nastepna transakcja', { icon: 'warning', duration: 8000 });
          }
        } catch (updateErr) {
          console.error('Blad aktualizacji statusu:', updateErr);
          toast.error('Paragon wydrukowany, ale nie udalo sie zaktualizowac statusu w bazie.');
        }
      } else if (result.printed === 'no') {
        // Drukarka odpowiedziala ale paragonu NIE ma -> na pewno NIE wyszedl. Bezpieczne ponowienie.
        const errMsg = result.errorMessage || result.error || 'Nieznany blad';
        try {
          const bladDesc = result.errorCode ? `[${result.errorCode}] ${errMsg}` : errMsg;
          await transactionService.updateFiscalStatus(transaction.id, false, bladDesc);
        } catch (e) {}
        toast.error(`Paragon NIE zostal wydrukowany: ${errMsg}. Ponow (retry bezpieczny).`, { duration: 12000 });
      } else {
        // 'unknown' -> niepewne. NIE fiskalizuj, NIE ponawiaj na slepo.
        const errMsg = result.errorMessage || result.error || 'Drukarka nie potwierdzila wydruku';
        try {
          const bladDesc = result.errorCode ? `[${result.errorCode}] ${errMsg}` : errMsg;
          await transactionService.updateFiscalStatus(transaction.id, false, bladDesc);
        } catch (e) {}
        toast('Nie potwierdzono wydruku paragonu. Sprawdz drukarke przed ponowieniem.', { icon: '⚠️', duration: 12000 });
      }
    } catch (err) {
      console.error('Blad fiskalizacji:', err);
      toast.error(`Blad fiskalizacji: ${err.message}`);
    }
  }, [drukujZKontraktu]);

  return {
    connected,
    loading,
    autoPrint,
    setAutoPrint,
    isSupported,
    getProtocol,
    connect,
    disconnect,
    printReceipt,
    printFromTransactionList,
    service: fiscalPrinterService
  };
};

export default useFiscalPrinter;
